import { Box, ClickAwayListener, Stack, Typography } from '@mui/material';
import { DiagnosesItem, SelectebleDiagnoses } from '../../DiagnosesInput.components';
import { buildDataTestId, hexOpacity, scrollBarSM } from '@src/utils';
import { PreloaderBubbles } from '@components/PreloaderBubbles';
import { theme } from '@src/theme';
import { FC, useState } from 'react';
import { diagnosesApiSlice } from '@src/api';
import { useDebounce } from '@src/hooks/useDebounce';

const initialFilter = {
  search: '',
  selectable: false,
};

type DiagnosesPaperProps = {
  search: string;
  allDiagnoses: any[];
  setSelectedDiagnosesLocal?: any;
  dataTestId?: string;
};

export const DiagnosesPaper: FC<DiagnosesPaperProps> = ({
  search,
  allDiagnoses,
  setSelectedDiagnosesLocal,
  dataTestId,
}) => {
  const [allSelectableDiagnoses, setAllSelectableDiagnoses] = useState<SelectebleDiagnoses>(null);
  const debouncedSearch = useDebounce(search);

  const { data: diagnoses, isLoading } = diagnosesApiSlice.useGetUnselectableDiagnosesQuery({
    ...initialFilter,
    search: debouncedSearch || '',
  });

  return (
    <Box position="relative" overflow="auto" height="calc(100% - 55px)" sx={scrollBarSM}>
      <ClickAwayListener onClickAway={() => setAllSelectableDiagnoses(null)}>
        {allSelectableDiagnoses?.data?.length ? (
          <Box
            position="fixed"
            zIndex={10}
            width="50%"
            height="calc(100% - 58px)"
            right="0"
            bgcolor="#fff"
            sx={{ boxShadow: '-6px 0px 36px 4px #00000026' }}
          >
            <Stack sx={{ overflow: 'auto', maxHeight: 'calc(100% - 58px)', ...scrollBarSM }}>
              {allSelectableDiagnoses.data.map((selectableDiagnosis, index) => (
                <DiagnosesItem
                  withoutIcon
                  additionalСonditionCheckbox={allSelectableDiagnoses.parentChecked}
                  key={selectableDiagnosis.id}
                  level={0}
                  diagnosis={selectableDiagnosis}
                  setAllSelectableDiagnoses={setAllSelectableDiagnoses}
                  searchDiagnosesValue={debouncedSearch}
                  checked={
                    allDiagnoses.some(({ id }) => id === selectableDiagnosis.id) || allSelectableDiagnoses.parentChecked
                  }
                  setSelectedDiagnosesLocal={setSelectedDiagnosesLocal}
                  brothers={allSelectableDiagnoses.brothers}
                  allDiagnoses={allDiagnoses}
                  generalParentIds={[...(allSelectableDiagnoses.generalParentIds || []), selectableDiagnosis.id]}
                  parentDiagnosis={allSelectableDiagnoses.parentDiagnosis}
                  dataTestId={buildDataTestId(dataTestId, `selectable-diagnosis-${index}`)}
                />
              ))}
            </Stack>
          </Box>
        ) : (
          <></>
        )}
      </ClickAwayListener>

      <Box
        borderBottom={`1px solid ${hexOpacity(theme.palette.secondary.main, '14%')}`}
        position="relative"
        height="100%"
      >
        {isLoading && <PreloaderBubbles overlay="light" size={16} />}
        {diagnoses?.data && diagnoses?.data.length > 0
          ? diagnoses.data.map((diagnosis, index) => (
              <DiagnosesItem
                key={diagnosis.id}
                level={0}
                diagnosis={diagnosis}
                setAllSelectableDiagnoses={setAllSelectableDiagnoses}
                searchDiagnosesValue={debouncedSearch}
                checked={allDiagnoses.some(({ id }) => id === diagnosis.id)}
                setSelectedDiagnosesLocal={setSelectedDiagnosesLocal}
                brothers={[]}
                allDiagnoses={allDiagnoses}
                generalParentIds={[diagnosis.id]}
                dataTestId={buildDataTestId(dataTestId, `diagnosis-${index}`)}
              />
            ))
          : !isLoading && (
              <Box padding="24px">
                <Typography color="grey.500" variant="label14Medium">
                  Diagnosis not found. Try to change search request.
                </Typography>
              </Box>
            )}
      </Box>
    </Box>
  );
};
